import React, {Fragment, RefObject} from 'react';
import {MapViewState} from "@deck.gl/core";
import {Map} from "react-map-gl";
import DeckGL from "@deck.gl/react";
import {getMapBoxToken} from "./map/MapUtils";
import {DeckGLRef, FlyToInterpolator, Layer, MapView, WebMercatorViewport} from "deck.gl";
import {defineMapComponent} from "./store/Accessor";
import {LayerDef, MapStyle} from "./types";
import {getAppState} from "./store/AppState";
import {Legend} from "./components/Legend";
import {MapOverlay} from "./map/MapOverlay";
import {XIcon} from "./ui/XIcon";
import {IconInfo} from "./util/IconInfo";
import {isValidNumber} from "./util/CommonUtil";
import {DeckMapFactory} from "./map/deck/DeckMapFactory";


interface MapComponentProps {
}


interface MapComponentState {
    layers: Layer[];
    mapViewState: MapViewState;
    tooltip: JSX.Element;
    isPinnedTooltip: boolean;
}


export class MapComponent extends React.Component<MapComponentProps, MapComponentState> {

    private ref: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
    private deckRef: RefObject<DeckGLRef> = React.createRef<DeckGLRef>();

    private deckFactory: DeckMapFactory = new DeckMapFactory();

    constructor(props: MapComponentProps) {
        super(props);
        defineMapComponent(this);
        this.state = this.initState();
    }

    is2D = () => {
        return this.state.mapViewState.pitch === 0;
    }

    set2D = () => {
        this.setState({mapViewState: {...this.state.mapViewState, pitch: 0}});
    }

    set3D = () => {
        if (this.is2D()) {
            this.setState({mapViewState: {...this.state.mapViewState, pitch: 45}})
        }
    }

    private createLayer(def: LayerDef): Layer {
        return this.deckFactory.createLayer(
            def,
            () => !this.state.tooltip || !this.state.isPinnedTooltip,
            (tt, isPinned) => this.setTooltip(tt, isPinned)
        )
    }

    private initState = (): MapComponentState => {
        const appState = getAppState();
        const mapView = appState.getMapView();
        return {
            layers: appState.getLayerDefs().map(def => this.createLayer(def)),
            mapViewState: {
                longitude: mapView?.longitude ?? 19.87,
                latitude: mapView?.latitude ?? 44.28,
                minZoom: 1,
                zoom: mapView?.zoom ?? 1,
                pitch: mapView?.pitch ?? 0,
                bearing: mapView?.bearing ?? 0
            },
            tooltip: null,
            isPinnedTooltip: false,
        };
    }

    resetState = () => {
        this.setState( this.initState );
    }

    recreateLayers = () => {
        this.setState( {layers: getAppState().getLayerDefs().map(def => this.createLayer(def))} );
    }

    moveLayer = (fromIndex: number, toIndex: number): void => {
        const newLayers = [...this.state.layers];
        const layer = newLayers[fromIndex];
        newLayers.splice(fromIndex, 1);
        newLayers.splice(toIndex, 0, layer);
        this.setState( {layers: newLayers} );
    }

    addLayer = (layer: LayerDef) => {
        this.setState({layers: [...this.state.layers, this.createLayer(layer)]});
    }

    updateExistingLayer = (layerDef: LayerDef) => {
        const newLayers = this.state.layers.map(layer =>
            this.deckFactory.matchesLayerDef(layer, layerDef) ?
                layer.clone({ visible: !layerDef.isHidden, opacity: layerDef.config.opacity / 100 }) : layer
        );
        this.setState({layers: newLayers});
    }

    updateLayers(...layerDefs: LayerDef[]) {
        const newLayers = [];
        for (const layer of this.state.layers) {
            const layerDef = layerDefs.find(def => this.deckFactory.matchesLayerDef(layer, def));
            newLayers.push(layerDef ? this.createLayer(layerDef) : layer);
        }
        this.setState({layers: newLayers});
    }

    deleteLayer(layerDef: LayerDef) {
        const newLayers = this.state.layers.filter(layer => !this.deckFactory.matchesLayerDef(layer, layerDef));
        this.setState({layers: newLayers});
    }

    private onMapViewStateChange = (view) => {
        const appState = getAppState();
        const viewState = view.viewState;
        appState.setMapView({
            ...appState.getMapView(),
            latitude: viewState.latitude,
            longitude: viewState.longitude,
            zoom: viewState.zoom,
            pitch: viewState.pitch,
            bearing: viewState.bearing,
        });
        this.setState({
            mapViewState: viewState,
            tooltip: null
        });
    }

    public setTooltip(tooltip: JSX.Element, isPinned: boolean = false) {
        this.setState({tooltip: tooltip, isPinnedTooltip: isPinned});
    }

    private getMapBoxMapStyle = (): string => {
        let appState = getAppState();
        const style = appState.getMapStyle();
        switch (style) {
            case MapStyle.terrain:
                return "mapbox://styles/mapbox/standard";
            case MapStyle.satellite:
                return "mapbox://styles/mapbox/standard-satellite";
            default:
                return appState.isDarkTheme() ? "mapbox://styles/mapbox/dark-v11" : "mapbox://styles/mapbox/light-v11";
        }
    }

    public flyTo = ([lon, lat]: [number, number]) => {
        if (isValidNumber(lon) && isValidNumber(lat)) {
            this.setState({
                mapViewState: {
                    ...this.state.mapViewState,
                    longitude: lon,
                    latitude: lat,
                    transitionInterpolator: new FlyToInterpolator({speed: 2}),
                    transitionDuration: 'auto',
                }
            });
        }
    };

    zoomToBounds = (bounds: [[number, number], [number, number]]): void => {
        const w = this.deckRef.current?.deck?.width;
        const h = this.deckRef.current?.deck?.height;
        if (w && h) {
            const padding = Math.max(30, Math.round(w * 0.033));
            const viewport = new WebMercatorViewport({width: w, height: h}).fitBounds(bounds, {padding: padding});

            this.setState({
                mapViewState: {
                    ...this.state.mapViewState,
                    longitude: viewport.longitude,
                    latitude: viewport.latitude,
                    zoom: viewport.zoom
                }
            });
        }
    }

    private renderMarkers = (): JSX.Element => {
        const markers = getAppState().getMarkers();
        if (markers && markers.length > 0) {
            const viewport = new WebMercatorViewport(this.state.mapViewState);
            const project = (coords: [number, number]) => {
                return viewport.project(coords);
            };

            return (
                <Fragment key={`map_${getAppState().getMarkers()}`} >
                    {
                        markers.map(markerInfo => {
                            const [lon, lat] = markerInfo.getDataRow().getLonLat();
                            if (isValidNumber(lon) && isValidNumber(lat)) {
                                return (
                                    <MapOverlay
                                        lonLat={[lon, lat]}
                                        content={<XIcon icon={IconInfo.PIN} iconSize={"1.5em"} fill={"red"}
                                                        onClick={() => {}}/>}
                                        projectFunc={project}
                                    />
                                )
                            }
                            return null;
                        })
                    }
                </Fragment>
            )
        }
        return null;
    }

    render = () => {
        let appState = getAppState();

        const closeUnpinnedTooltipIfNeeded = event => {
            if (this.state.tooltip && !this.state.isPinnedTooltip) {
                this.setTooltip(null);
            }
        };

        const closePinnedTooltipOnEscape = event => {
            if (event.key === "Escape" && this.state.tooltip && this.state.isPinnedTooltip) {
                this.setTooltip(null);
                event.stopPropagation();
            }
        };

        return (
            <div ref={this.ref} onMouseLeave={closeUnpinnedTooltipIfNeeded} onKeyDown={closePinnedTooltipOnEscape}>
                <DeckGL
                    ref={this.deckRef}
                    onViewStateChange={view => this.onMapViewStateChange(view)}
                    controller={true}
                    viewState={this.state.mapViewState}
                    views={new MapView({ repeat: true })}
                    layers={this.state.layers}
                    getCursor={cursorState => {
                        return cursorState.isHovering ? 'pointer' : 'grab'
                    }}
                    onClick={(info, event) => {
                        if (!info.picked && this.state.tooltip && !event["consumed"]) {
                            this.setTooltip(null);
                        }
                    }}
                >
                    <Map
                        reuseMaps
                        mapStyle={this.getMapBoxMapStyle()}
                        projection={{name: "mercator"}}
                        mapboxAccessToken={getMapBoxToken()}
                    />
                    { this.renderMarkers() }
                    {!this.state.isPinnedTooltip && this.state.tooltip}
                </DeckGL>
                { appState.isShowLegend() && <Legend /> }
                {this.state.isPinnedTooltip && this.state.tooltip}
            </div>
        );
    }

}